import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { X } from 'lucide-react';
import { RouteTransitionLogo } from '../../components/RouteTransitionLogo';
import { CameraQrScannerModal } from '../../components/CameraQrScannerModal';
import ScanNumericKeypad from '../../components/scan/ScanNumericKeypad';
import InfoPopoverButton from '../../components/InfoPopoverButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const FONT = "'Inria Serif', serif";

export default function DemoScanPage() {
  const navigate = useNavigate();
  const [amount, setAmount] = useState('');
  const [showScanner, setShowScanner] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [scanning, setScanning] = useState(false);

  const canScan = amount && parseFloat(amount) > 0;

  const handleScan = () => {
    if (!canScan) {
      toast.error('Please enter the total bill amount first');
      return;
    }
    setShowScanner(true);
  };

  const handleSubmitQR = async (codeValue = qrCode) => {
    const resolvedCode = (codeValue || '').trim();
    if (!resolvedCode) {
      toast.error('Please scan or enter the demo QR code');
      return false;
    }
    setScanning(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/demo-transactions`,
        { original_amount: parseFloat(amount), qr_payload: resolvedCode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
        try { navigator.vibrate(15); } catch (_) { /* noop */ }
      }
      toast.success('Demo QR scanned! Awaiting guest...');
      navigate(`/demo-scan/awaiting/${res.data.id}`);
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Not a demo QR code');
      return false;
    } finally {
      setScanning(false);
    }
  };

  return (
    <div className="app-shell" data-testid="demo-scan-page" style={{ minHeight: '100vh', backgroundColor: 'var(--app-bg)', display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 'calc(env(safe-area-inset-top, 0px) + 56px)', paddingBottom: 60, position: 'relative' }}>
      <button
        type="button"
        onClick={() => navigate('/demo-scan')}
        aria-label="Close demo scan"
        data-testid="demo-scan-close"
        style={{ position: 'absolute', top: 'max(14px, env(safe-area-inset-top))', right: 18, width: 40, height: 40, borderRadius: '50%', background: 'var(--surface)', border: '1px solid var(--card-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 2 }}
      >
        <X size={20} color="var(--text)" />
      </button>

      <div className="scan-logo-halo" style={{ marginTop: 8 }}>
        <RouteTransitionLogo width={96} height={86} dataTestId="demo-scan-logo" />
      </div>

      <div className={`scan-amount-card${canScan ? ' is-filled' : ''}`} style={{ marginTop: 22 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <label htmlFor="demo-scan-amount" style={{ fontFamily: FONT, fontSize: 12, fontWeight: 600, color: 'var(--text-3)', letterSpacing: 1.6, textTransform: 'uppercase' }}>
            Demo bill amount
          </label>
          <InfoPopoverButton
            testId="demo-scan-amount-info"
            size="sm"
            align="center"
            title="Demo bill"
            tip="This is a practice run. No real money is charged — type any amount and tap the QR button."
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 6, marginTop: 14, padding: '0 10px' }}>
          <input
            id="demo-scan-amount"
            type="text"
            inputMode="none"
            readOnly
            autoComplete="off"
            value={amount}
            onFocus={(e) => e.target.blur()}
            placeholder="0"
            data-testid="demo-scan-amount-input"
            className="scan-amount-field"
            style={{ width: 200, fontFamily: FONT, fontSize: 64, fontWeight: 700, color: canScan ? 'var(--text)' : 'var(--text-3)', textAlign: 'right', backgroundColor: 'transparent', border: 'none', outline: 'none', padding: 0, letterSpacing: -0.5, caretColor: 'transparent', cursor: 'default', transition: 'color 200ms ease' }}
          />
          <span style={{ fontFamily: FONT, fontSize: 30, fontWeight: 500, color: canScan ? 'var(--text)' : 'var(--text-3)', transition: 'color 200ms ease' }}>€</span>
        </div>

        <div style={{ marginTop: 10, padding: '0 6px' }}>
          <div className="scan-amount-underline" />
        </div>
      </div>

      <div className="scan-cta-caption" style={{ marginTop: 24, marginBottom: 0 }}>
        <p style={{ fontFamily: FONT, fontSize: 14, fontWeight: 500, color: 'var(--text-3)', textAlign: 'center', margin: 0, letterSpacing: 0.2 }}>
          Type amount, then tap scan
        </p>
      </div>

      <ScanNumericKeypad
        value={amount}
        onChange={setAmount}
        onScan={handleScan}
        canScan={!!canScan}
        testIdPrefix="demo-scan"
      />

      <p style={{ marginTop: 'auto', paddingTop: 24, fontFamily: FONT, fontSize: 11, color: 'var(--text-3)', textAlign: 'center' }}>
        Demo mode · No real payment is processed.
      </p>

      <CameraQrScannerModal
        isOpen={showScanner}
        onClose={() => setShowScanner(false)}
        title="Scan Demo QR Code"
        manualValue={qrCode}
        onManualChange={setQrCode}
        onScanCode={handleSubmitQR}
        scanning={scanning}
        modalTestId="demo-scan-camera-modal"
        manualInputTestId="demo-scan-manual-input"
        confirmButtonTestId="demo-scan-submit-qr"
      />
    </div>
  );
}
